/**
 * Livello flottante ancorato a un bottone (fix popover «Filtri»/«Viste» della
 * coda v4). Il contenuto (di norma un `S3Menu`) viene reso in un portal dentro
 * la radice `.sup4` piu vicina (fallback `document.body`, con `sup3 sup4` per
 * token e classi) e in una scatola `position: fixed` sovrapposta al bottone:
 * nessun antenato con `overflow` (la card della coda) puo tagliarlo e nessuna
 * riga della tabella gli passa sopra. La scatola ricalca il rettangolo del
 * bottone, cosi l'ancoraggio di `S3Menu` (right 0, top 40) resta quello di
 * prima; non intercetta i clic (`pointer-events: none`, il menu li riprende).
 */
import React, { useCallback, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface Rect { top: number; left: number; width: number; height: number }

const measure = (el: HTMLElement | null): Rect | null => {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

/** Il menu parte 40 px sotto il bordo alto del bottone: l'altezza massima resta nel viewport (minimo 200 px). */
const boxStyle = (r: Rect): React.CSSProperties =>
  ({
    top: r.top,
    left: r.left,
    width: r.width,
    height: r.height,
    '--s4-pop-max': `${Math.max(200, window.innerHeight - r.top - 52)}px`,
  }) as React.CSSProperties;

const AnchoredLayer: React.FC<{
  open: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  /** Classi della scatola (es. `s4-pop s4-pop--filters`). */
  className: string;
  children: React.ReactNode;
}> = ({ open, anchorRef, className, children }) => {
  const [rect, setRect] = useState<Rect | null>(() => (open ? measure(anchorRef.current) : null));

  const update = useCallback(() => setRect(measure(anchorRef.current)), [anchorRef]);

  useLayoutEffect(() => {
    if (!open) return;
    update();
    // Cattura: segue anche lo scorrimento dei contenitori interni, non solo della finestra.
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, update]);

  if (!open) return null;
  const host = (anchorRef.current?.closest('.sup4') as HTMLElement | null) || null;
  const box = (
    <div
      className={`${className} s4-pop--layer`}
      data-testid="s4-pop-layer"
      style={rect ? boxStyle(rect) : undefined}
    >
      {children}
    </div>
  );
  // Area associativa: la radice `.sup4` e la pagina, mentre `.sup3` (token e
  // classi `.s3-*` del menu) sta solo sulla card della coda: senza questo
  // involucro il pannello resterebbe trasparente, fuori posto e illeggibile in scuro.
  const content = !host ? <div className="sup3 sup4">{box}</div> : host.closest('.sup3') ? box : <div className="sup3">{box}</div>;
  return createPortal(content, host || document.body);
};

export default AnchoredLayer;
